<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue';
import { X as XIcon } from 'lucide-vue-next';

const props = withDefaults(
  defineProps<{
    open: boolean;
    title?: string;
    maxWidth?: 'sm' | 'md';
  }>(),
  { title: '', maxWidth: 'md' },
);

const emit = defineEmits<{ close: [] }>();

const widths: Record<'sm' | 'md', string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
};

const onKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') emit('close');
};

// Only listen while open, so a background modal never eats another one's Escape.
watch(
  () => props.open,
  (open) => {
    if (open) {
      window.addEventListener('keydown', onKeydown);
    } else {
      window.removeEventListener('keydown', onKeydown);
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <!-- Teleported to the body: the app header is `sticky z-50`, so a modal rendered
       inside a view would share its stacking tier and depend on DOM order. -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-center justify-center px-4"
        role="dialog"
        aria-modal="true"
      >
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="emit('close')" />
        <div
          :class="[
            'relative z-10 w-full bg-[#111113] border border-[#2A2018] rounded-2xl p-6 shadow-2xl',
            widths[maxWidth],
          ]"
        >
          <div v-if="title" class="flex items-center justify-between mb-6">
            <h3 class="text-xl font-semibold text-[#F5F0EA]">{{ title }}</h3>
            <button
              type="button"
              class="p-2 text-[#524840] hover:text-[#F5F0EA] hover:bg-[#1E1E23] rounded-lg transition"
              @click="emit('close')"
            >
              <XIcon class="w-5 h-5" />
            </button>
          </div>
          <slot />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
